import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/colors';
import { useRSVP } from '../../contexts/RSVPContext';
import { EventResponses, RSVPData, RSVPEventId } from '../../services/api';

const RSVP_EVENTS: { id: RSVPEventId; title: string; time: string }[] = [
  {
    id: 'welcomeDrinks',
    title: 'Welcome Drinks',
    time: 'Thursday, 6:30 PM'
  },
  {
    id: 'poolParty',
    title: 'Pool Party',
    time: 'Friday, 12:00 PM'
  },
  {
    id: 'rehearsalDinner',
    title: 'Rehearsal Dinner',
    time: 'Friday, 6:00 PM'
  },
  {
    id: 'ceremony',
    title: 'Ceremony & Reception',
    time: 'Saturday, 4:30 PM'
  }
];

type RSVPFormStep = 'attendance' | 'events';
type PartyAttendance = { name: string; attending: boolean | null };

const buildEmptyPartyAttendance = (names: string[]): PartyAttendance[] => (
  names.map(name => ({ name, attending: null }))
);

const buildEmptyEventResponses = (names: string[]): EventResponses => ({
  welcomeDrinks: {
    partyMembers: names.map(name => ({ name, attending: false }))
  },
  poolParty: {
    partyMembers: names.map(name => ({ name, attending: false }))
  },
  rehearsalDinner: {
    partyMembers: names.map(name => ({ name, attending: false }))
  },
  ceremony: {
    partyMembers: names.map(name => ({ name, attending: false }))
  }
});

const ErrorMessage = styled.div`
  color: ${colors.api.error};
  font-family: 'GremeryTwo', cursive;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
  font-style: italic;
  opacity: 0.9;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  font-family: 'GremeryTwo', cursive;
  color: ${colors.neutral.darkBrown};
`;

const ThankYouContainer = styled.div`
  text-align: center;
  color: ${colors.neutral.darkBrown};
  margin-bottom: 2rem;
`;

const ThankYouTitle = styled.div`
  font-family: 'saint-andrews-queen', serif;
  font-size: 4.5rem;
  line-height: 1;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 3.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2.8rem;
    margin-bottom: 0.75rem;
  }
`;

const ThankYouMessage = styled.div`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 1rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.3;
    padding: 0 1rem;
  }
`;

const GuestInput = styled.input`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryTwo', cursive;
  transition: all 0.3s ease;
  color: #2c3e50;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryTwo', cursive;
  }
`;

const SelectedGuestInfo = styled.div`
  padding: 1rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid ${colors.primary.gold};
  margin-bottom: 1rem;

  @media (max-width: 600px) {
    align-items: flex-start;
    border-bottom-width: 1px;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
    padding: 0.75rem 0;
  }
`;

const GuestName = styled.span`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.1rem;
  color: #2c3e50;
  line-height: 1.35;
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.neutral.darkBrown};
  padding: 0.25rem 0;
  border-radius: 0;
  font-size: 0.95rem;
  font-family: 'GremeryTwo', cursive;
  font-weight: 400;
  cursor: pointer;
  transition: opacity 0.3s ease;
  letter-spacing: 0.25px;
  border: none;
  opacity: 0.55;
  
  &:hover {
    opacity: 0.9;
  }
`;

const SearchButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 1rem;
  width: 100%;
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: opacity 0.3s ease;
  position: relative;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const SearchButtonImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const SearchButtonText = styled.span`
  position: absolute;
  top: 52%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${colors.primary.lightTan};
  font-family: 'GremeryOne', serif;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 1px;
  pointer-events: none;
  white-space: nowrap;

  @media (max-width: 480px) {
    font-size: 0.95rem;
    letter-spacing: 0.5px;
  }
`;

const BackButton = styled.button`
  align-self: flex-start;
  background: transparent;
  border: none;
  color: ${colors.neutral.darkBrown};
  cursor: pointer;
  font-family: 'GremeryTwo', cursive;
  font-size: 1rem;
  padding: 0;
  opacity: 0.65;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const FormContainer = styled.div`
  background: transparent;
  border-radius: 20px;
  padding: 3rem;
  border: none;
  backdrop-filter: none;
  box-shadow: none;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.25rem 0.75rem 2rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  @media (max-width: 600px) {
    gap: 1.15rem;
  }
`;

const PartyMemberRow = styled.div`
  margin-bottom: 0.5rem;

  @media (max-width: 600px) {
    margin-bottom: 0.25rem;
  }
`;

const EventSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const EventSelectionSection = styled(EventSection)`
  padding-bottom: 1.25rem;

  & + & {
    margin-top: 1rem;
  }
`;

const EventDescription = styled.p`
  font-family: 'GremeryTwo', cursive;
  color: rgba(28, 25, 18, 0.68);
  font-size: 0.95rem;
  line-height: 1.4;
  margin: -0.25rem 0 0.25rem;

  @media (max-width: 600px) {
    font-size: 0.9rem;
    margin-top: -0.15rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;

  @media (max-width: 600px) {
    gap: 0.65rem;
  }
`;

const Label = styled.label`
  font-family: 'GremeryTwo', cursive;
  font-size: 1.25rem;
  color: ${colors.neutral.darkBrown};
  font-weight: 600;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  letter-spacing: 0.3px;

  @media (max-width: 600px) {
    font-size: 1.1rem;
    margin-bottom: 0.15rem;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: -15px;
    width: 100%;
    height: 15px;
    background-image: url('/images/underline.png');
    background-repeat: no-repeat;
    background-size: 100% 100%;
    background-position: center bottom;
    opacity: 0.4;
    pointer-events: none;
  }
`;

const TextArea = styled.textarea`
  padding: 0.75rem 0 1.25rem 0;
  border: none;
  background: transparent;
  font-size: 1rem;
  font-family: 'GremeryOne', cursive;
  line-height: 1.5;
  min-height: 80px;
  resize: none;
  transition: all 0.3s ease;
  color: #2c3e50;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: #7f8c8d;
    opacity: 0.8;
    font-family: 'GremeryOne', cursive;
  }
`;

const AttendanceOptions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;

  @media (max-width: 600px) {
    gap: 0.5rem;
  }
`;

const AttendanceInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

const AttendanceOption = styled.label<{ $checked: boolean }>`
  position: relative;
  min-width: 92px;
  padding: 0.65rem 1.15rem;
  border: 1px solid ${({ $checked }) => (
    $checked ? colors.primary.gold : 'rgba(28, 25, 18, 0.22)'
  )};
  border-radius: 999px;
  background: ${({ $checked }) => (
    $checked ? 'rgba(180, 139, 68, 0.18)' : 'rgba(255, 255, 255, 0.2)'
  )};
  color: ${colors.neutral.darkBrown};
  cursor: pointer;
  font-family: 'GremeryTwo', cursive;
  font-size: 1rem;
  line-height: 1;
  text-align: center;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.primary.gold};
    background: rgba(180, 139, 68, 0.12);
  }

  &:has(input:focus-visible) {
    outline: 2px solid ${colors.primary.gold};
    outline-offset: 3px;
  }

  @media (max-width: 600px) {
    flex: 1;
    min-width: 0;
  }
`;

const AttendanceMemberRow = styled(PartyMemberRow)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;

  ${AttendanceOptions} {
    flex-shrink: 0;
    margin-top: 0;
  }

  @media (max-width: 600px) {
    align-items: stretch;
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const EventGuestList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RSVPFields = styled.div`
  margin-top: 2rem;

  @media (max-width: 600px) {
    margin-top: 1rem;
  }
`;

const RSVPFormComponent: React.FC = () => {
  const {
    selectedGuest,
    currentRSVP,
    loadingGuestSearch,
    loadingRSVP,
    submittingRSVP,
    guestSearchError,
    rsvpError,
    searchGuest,
    submitRSVP,
    updateRSVP,
    clearSelection
  } = useRSVP();

  const [guestName, setGuestName] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [formStep, setFormStep] = useState<RSVPFormStep>('attendance');
  const [eventSelectionMade, setEventSelectionMade] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState({
    partyAttendance: buildEmptyPartyAttendance([]),
    eventResponses: buildEmptyEventResponses([]),
    message: ''
  });

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedAttendance, setSubmittedAttendance] = useState<'yes' | 'no' | null>(null);

  // Populate the editable reservation when a party is found.
  useEffect(() => {
    if (!selectedGuest) {
      setFormStep('attendance');
      setEventSelectionMade({});
      setFormData({
        partyAttendance: buildEmptyPartyAttendance([]),
        eventResponses: buildEmptyEventResponses([]),
        message: ''
      });
      return;
    }

    const fallbackCeremonyMembers = currentRSVP?.partyMembers || [];
    const eventResponses = RSVP_EVENTS.reduce((responses, event) => {
      const savedEvent = currentRSVP?.eventResponses?.[event.id];
      const savedMembers = savedEvent?.partyMembers
        || (event.id === 'ceremony' ? fallbackCeremonyMembers : []);

      responses[event.id] = {
        partyMembers: selectedGuest.searchableNames.map(name => {
          const savedMember = savedMembers.find(member => member.name === name);

          return {
            name,
            attending: savedMember?.attending ?? false
          };
        })
      };

      return responses;
    }, {} as EventResponses);

    const hasSavedResponse = Boolean(currentRSVP);
    const partyAttendance = selectedGuest.searchableNames.map(name => {
      const attendingAnyEvent = RSVP_EVENTS.some(event => (
        eventResponses[event.id].partyMembers.some(member => member.name === name && member.attending)
      ));
      const savedPartyMember = currentRSVP?.partyMembers?.find(member => member.name === name);

      return {
        name,
        attending: hasSavedResponse ? (attendingAnyEvent || savedPartyMember?.attending || false) : null
      };
    });

    const answeredEventSelections = hasSavedResponse
      ? RSVP_EVENTS.reduce((selections, event) => {
        const hasSavedEvent = Boolean(currentRSVP?.eventResponses?.[event.id]) || event.id === 'ceremony';
        if (!hasSavedEvent) {
          return selections;
        }

        partyAttendance.forEach(member => {
          if (member.attending) {
            selections[`${event.id}-${member.name}`] = true;
          }
        });

        return selections;
      }, {} as Record<string, boolean>)
      : {};

    setFormStep('attendance');
    setEventSelectionMade(answeredEventSelections);
    setFormData({
      partyAttendance,
      eventResponses,
      message: ''
    });
  }, [selectedGuest, currentRSVP]);

  const handleGuestSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    
    setHasSearched(true);
    setSubmitSuccess(false);
    setSubmittedAttendance(null);
    setEventSelectionMade({});
    await searchGuest(guestName.trim());
  };

  const handleGuestNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setGuestName(e.target.value);
    setSubmitSuccess(false);
    setSubmittedAttendance(null);
    setEventSelectionMade({});
    if (hasSearched) {
      clearSelection();
      setHasSearched(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartyAttendanceChange = (memberName: string, attending: boolean) => {
    if (!attending) {
      setEventSelectionMade(prev => {
        const next = { ...prev };
        RSVP_EVENTS.forEach(event => {
          delete next[`${event.id}-${memberName}`];
        });
        return next;
      });
    }

    setFormData(prev => ({
      ...prev,
      partyAttendance: prev.partyAttendance.map(member => (
        member.name === memberName ? { ...member, attending } : member
      )),
      eventResponses: RSVP_EVENTS.reduce((responses, event) => {
        responses[event.id] = {
          partyMembers: prev.eventResponses[event.id].partyMembers.map(member => (
            member.name === memberName && !attending ? { ...member, attending: false } : member
          ))
        };

        return responses;
      }, {} as EventResponses)
    }));
  };

  const handleEventMemberChange = (eventId: RSVPEventId, memberName: string, attending: boolean) => {
    setEventSelectionMade(prev => ({
      ...prev,
      [`${eventId}-${memberName}`]: true
    }));

    setFormData(prev => ({
      ...prev,
      eventResponses: {
        ...prev.eventResponses,
        [eventId]: {
          ...prev.eventResponses[eventId],
          partyMembers: prev.eventResponses[eventId].partyMembers.map(member => (
            member.name === memberName ? { ...member, attending } : member
          ))
        }
      }
    }));
  };

  const allPartyMembersAnswered = formData.partyAttendance.every(member => member.attending !== null);
  const attendingPartyMembers = formData.partyAttendance.filter(member => member.attending === true);
  const attendingPartyMemberNames = new Set(attendingPartyMembers.map(member => member.name));
  const hasAttendingPartyMembers = attendingPartyMembers.length > 0;
  const allEventSelectionsAnswered = RSVP_EVENTS.every(event => (
    attendingPartyMembers.every(member => eventSelectionMade[`${event.id}-${member.name}`])
  ));

  const handleAttendanceStepSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!allPartyMembersAnswered) {
      return;
    }

    if (hasAttendingPartyMembers) {
      setFormStep('events');
      return;
    }

    await handleSubmit(e);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedGuest) {
      alert('Please select a guest first.');
      return;
    }

    setSubmitSuccess(false);
    setSubmittedAttendance(null);

    const normalizedEventResponses = RSVP_EVENTS.reduce((responses, event) => {
      const eventResponse = formData.eventResponses[event.id];

      responses[event.id] = {
        partyMembers: eventResponse.partyMembers.map(member => ({
          ...member,
          attending: attendingPartyMemberNames.has(member.name) ? member.attending : false
        }))
      };

      return responses;
    }, {} as EventResponses);

    const overallAttendance = Object.values(normalizedEventResponses).some(event => (
      event.partyMembers.some(member => member.attending)
    )) ? 'yes' : 'no';
    const ceremonyPartyMembers = normalizedEventResponses.ceremony.partyMembers;

    const rsvpData: RSVPData = {
      guestId: selectedGuest.id,
      attendance: overallAttendance,
      partyMembers: ceremonyPartyMembers,
      eventResponses: normalizedEventResponses,
      message: formData.message
    };

    let success = false;
    if (currentRSVP) {
      // Update existing RSVP
      success = await updateRSVP(currentRSVP.id, rsvpData);
    } else {
      // Submit new RSVP
      success = await submitRSVP(rsvpData);
    }

    if (success) {
      setSubmittedAttendance(overallAttendance);
      setSubmitSuccess(true);
    }
  };

  if (submitSuccess) {
    return (
      <FormContainer>
        <ThankYouContainer>
          <ThankYouTitle>Thank You!</ThankYouTitle>
          <ThankYouMessage>
            {submittedAttendance === 'no'
              ? 'your RSVP has been saved.'
              : "your RSVP has been saved. we can't wait to celebrate with you."}
          </ThankYouMessage>
        </ThankYouContainer>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      {(guestSearchError || rsvpError) && (
        <ErrorMessage>
          {guestSearchError || rsvpError}
        </ErrorMessage>
      )}

      {loadingGuestSearch ? (
        <LoadingMessage>Looking up your party...</LoadingMessage>
      ) : (
        <>
          <FormGroup>
            {selectedGuest ? (
              <SelectedGuestInfo>
                <GuestName>
                  {selectedGuest.fullName}
                  {selectedGuest.searchableNames.length > 1 && (
                    <>
                      <br />
                      <small style={{ opacity: 0.7, fontSize: '0.9em' }}>
                        Party includes: {selectedGuest.searchableNames.join(', ')}
                      </small>
                    </>
                  )}
                </GuestName>
                <ClearButton
                  type="button"
                  onClick={() => {
                    clearSelection();
                    setGuestName('');
                    setHasSearched(false);
                    setFormStep('attendance');
                    setEventSelectionMade({});
                    setSubmitSuccess(false);
                    setSubmittedAttendance(null);
                  }}
                >
                  search again
                </ClearButton>
              </SelectedGuestInfo>
            ) : (
              <form onSubmit={handleGuestSearch}>
                <InputWrapper>
                  <GuestInput
                    type="text"
                    id="guestName"
                    aria-label="Enter your full name"
                    value={guestName}
                    onChange={handleGuestNameChange}
                    placeholder="Enter your full name..."
                    disabled={loadingGuestSearch}
                    autoComplete="name"
                  />
                </InputWrapper>
                <SearchButtonWrapper>
                  <SearchButton 
                    type="submit" 
                    disabled={loadingGuestSearch || !guestName.trim()}
                  >
                    <SearchButtonImage src="/images/svgs/funky-save.svg" alt="" />
                    <SearchButtonText>
                      {loadingGuestSearch ? 'SEARCHING...' : 'SEARCH'}
                    </SearchButtonText>
                  </SearchButton>
                </SearchButtonWrapper>
              </form>
            )}
          </FormGroup>

          {selectedGuest && (
            <RSVPFields>
              {loadingRSVP ? (
                <LoadingMessage>Loading RSVP data...</LoadingMessage>
              ) : (
                <Form onSubmit={formStep === 'attendance' ? handleAttendanceStepSubmit : handleSubmit}>
                  {formStep === 'attendance' ? (
                    <FormGroup>
                      <Label>Will you be attending?</Label>
                      {formData.partyAttendance.map(member => (
                        <AttendanceMemberRow key={`attendance-${member.name}`}>
                          <GuestName>{member.name}</GuestName>
                          <AttendanceOptions>
                            <AttendanceOption $checked={member.attending === true}>
                              <AttendanceInput
                                type="radio"
                                name={`attendance-${member.name}`}
                                checked={member.attending === true}
                                onChange={() => handlePartyAttendanceChange(member.name, true)}
                                required
                              />
                              Yes
                            </AttendanceOption>
                            <AttendanceOption $checked={member.attending === false}>
                              <AttendanceInput
                                type="radio"
                                name={`attendance-${member.name}`}
                                checked={member.attending === false}
                                onChange={() => handlePartyAttendanceChange(member.name, false)}
                                required
                              />
                              No
                            </AttendanceOption>
                          </AttendanceOptions>
                        </AttendanceMemberRow>
                      ))}
                    </FormGroup>
                  ) : (
                    <>
                      {RSVP_EVENTS.map(event => (
                        <EventSelectionSection key={event.id}>
                          <Label>{event.title}</Label>
                          <EventDescription>{event.time}</EventDescription>
                          <EventGuestList>
                            {formData.eventResponses[event.id].partyMembers
                              .filter(member => attendingPartyMemberNames.has(member.name))
                              .map(member => (
                                <AttendanceMemberRow key={`${event.id}-${member.name}`}>
                                  <GuestName>{member.name}</GuestName>
                                  <AttendanceOptions>
                                    <AttendanceOption $checked={member.attending}>
                                      <AttendanceInput
                                        type="radio"
                                        name={`${event.id}-${member.name}`}
                                        checked={member.attending}
                                        onChange={() => handleEventMemberChange(event.id, member.name, true)}
                                      />
                                      Yes
                                    </AttendanceOption>
                                    <AttendanceOption $checked={Boolean(eventSelectionMade[`${event.id}-${member.name}`]) && !member.attending}>
                                      <AttendanceInput
                                        type="radio"
                                        name={`${event.id}-${member.name}`}
                                        checked={Boolean(eventSelectionMade[`${event.id}-${member.name}`]) && !member.attending}
                                        onChange={() => handleEventMemberChange(event.id, member.name, false)}
                                      />
                                      No
                                    </AttendanceOption>
                                  </AttendanceOptions>
                                </AttendanceMemberRow>
                              ))}
                          </EventGuestList>
                        </EventSelectionSection>
                      ))}

                      <FormGroup>
                        <Label htmlFor="message">special message (optional)</Label>
                        <InputWrapper>
                          <TextArea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder="Any special message for the couple..."
                          />
                        </InputWrapper>
                      </FormGroup>
                    </>
                  )}

                  {formStep === 'events' && (
                    <BackButton type="button" onClick={() => setFormStep('attendance')}>
                      back
                    </BackButton>
                  )}

                  <SearchButton
                    type="submit"
                    disabled={
                      submittingRSVP
                      || (formStep === 'attendance' && !allPartyMembersAnswered)
                      || (formStep === 'events' && !allEventSelectionsAnswered)
                    }
                  >
                    <SearchButtonImage src="/images/svgs/funky-save.svg" alt="" />
                    <SearchButtonText>
                      {submittingRSVP 
                        ? 'SUBMITTING...' 
                        : formStep === 'attendance' && hasAttendingPartyMembers
                          ? 'NEXT'
                          : currentRSVP 
                            ? 'UPDATE RSVP' 
                            : 'SUBMIT RSVP'}
                    </SearchButtonText>
                  </SearchButton>
                </Form>
              )}
            </RSVPFields>
          )}
        </>
      )}
    </FormContainer>
  );
};

export default RSVPFormComponent; 